import { useState, useEffect, useCallback } from "react";
import { authFetch, API } from "@/App";
import { Wrench, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";

const statusIcons = {
  RUNNING: { icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-600/20" },
  MAINTENANCE: { icon: Wrench, color: "text-amber-400", bg: "bg-amber-600/20" },
  IDLE: { icon: Clock, color: "text-slate-400", bg: "bg-slate-600/20" },
  BREAKDOWN: { icon: AlertTriangle, color: "text-red-400", bg: "bg-red-600/20" },
};

export default function EquipmentPage() {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadEquipment = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/factory/equipment`);
      const json = await res.json();
      if (json.success) setEquipment(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadEquipment(); }, [loadEquipment]);

  const updateStatus = async (eqId, newStatus) => {
    try {
      const res = await authFetch(`${API}/factory/equipment/${eqId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      const json = await res.json();
      if (json.success) { toast.success(`Status updated to ${newStatus}`); loadEquipment(); }
    } catch (e) { toast.error("Failed to update"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Equipment</h1>
        <p className="text-slate-400 text-sm mt-1">Monitor and maintain factory equipment ({equipment.length} items)</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {equipment.map((eq) => {
          const si = statusIcons[eq.status] || statusIcons.IDLE;
          const StatusIcon = si.icon;
          return (
            <div key={eq.equipment_id} className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-4" data-testid={`equipment-${eq.equipment_id}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium text-sm">{eq.name}</h3>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${si.bg} ${si.color}`}>
                  <StatusIcon className="w-3 h-3" /> {eq.status}
                </span>
              </div>
              <p className="text-slate-500 text-xs mb-3">{eq.type}</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Run Hours</span><span className="text-white">{(eq.hours_run || 0).toLocaleString()} hrs</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Last Service</span><span className="text-slate-300">{eq.last_maintenance || "-"}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Next Service</span><span className="text-slate-300">{eq.next_maintenance || "-"}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Health</span><span className={`font-medium ${eq.health >= 85 ? "text-emerald-400" : eq.health >= 70 ? "text-amber-400" : "text-red-400"}`}>{eq.health}%</span></div>
              </div>
              <div className="mt-3 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${eq.health >= 85 ? "bg-emerald-500" : eq.health >= 70 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${eq.health}%` }} />
              </div>
              <div className="mt-3 flex gap-2">
                {eq.status !== "RUNNING" && <button onClick={() => updateStatus(eq.equipment_id, "RUNNING")} className="px-2 py-1 bg-emerald-600/20 text-emerald-400 rounded text-xs hover:bg-emerald-600/30">Start</button>}
                {eq.status !== "MAINTENANCE" && <button onClick={() => updateStatus(eq.equipment_id, "MAINTENANCE")} className="px-2 py-1 bg-amber-600/20 text-amber-400 rounded text-xs hover:bg-amber-600/30">Maintenance</button>}
                {eq.status !== "IDLE" && <button onClick={() => updateStatus(eq.equipment_id, "IDLE")} className="px-2 py-1 bg-slate-600/20 text-slate-400 rounded text-xs hover:bg-slate-600/30">Stop</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
